/**
 * Synthesised sound effects (ADR-0009, docs/05-ui-design.md §7). No audio assets — every sound is
 * a couple of oscillators with a short envelope, so this file is the entire sound design. Rules
 * from the doc: only user-initiated completion events, never hover/focus; ordinary navigation and clicks stay silent; at most one
 * sound per 250 ms; muted until the first user gesture unlocks `AudioContext`.
 */

export const SOUND_NAMES = [
  'tick',
  'untick',
  'remove',
  'success',
  'payment',
  'contribution',
  'import-complete',
  'close-month',
  'swipe',
  'error',
  'celebrate',
] as const;
export type SoundName = (typeof SOUND_NAMES)[number];

interface Note {
  freq: number;
  start: number;
  duration: number;
  type?: OscillatorType;
  /** Peak gain for this note alone, before the user's volume preference is applied. */
  gain?: number;
}

// Chimes are 2–3 stacked notes in a major pentatonic (C D E G A), per the doc.
const SOUNDS: Record<SoundName, readonly Note[]> = {
  tick: [{ freq: 880, start: 0, duration: 0.08, type: 'sine', gain: 0.5 }],
  untick: [{ freq: 440, start: 0, duration: 0.08, type: 'sine', gain: 0.4 }],
  remove: [
    { freq: 440, start: 0, duration: 0.08, type: 'sine', gain: 0.2 },
    { freq: 330, start: 0.07, duration: 0.12, type: 'sine', gain: 0.15 },
  ],
  success: [
    { freq: 659.25, start: 0, duration: 0.09, type: 'triangle', gain: 0.4 },
    { freq: 880, start: 0.09, duration: 0.14, type: 'triangle', gain: 0.4 },
  ],
  // Routine completions stay softer and shorter than a milestone celebration.
  payment: [
    { freq: 659.25, start: 0, duration: 0.07, type: 'sine', gain: 0.24 },
    { freq: 880, start: 0.06, duration: 0.13, type: 'sine', gain: 0.2 },
  ],
  contribution: [
    { freq: 523.25, start: 0, duration: 0.08, type: 'triangle', gain: 0.2 },
    { freq: 659.25, start: 0.06, duration: 0.1, type: 'triangle', gain: 0.22 },
    { freq: 880, start: 0.13, duration: 0.14, type: 'triangle', gain: 0.25 },
  ],
  'import-complete': [
    { freq: 783.99, start: 0, duration: 0.07, type: 'sine', gain: 0.2 },
    { freq: 659.25, start: 0.07, duration: 0.07, type: 'sine', gain: 0.18 },
    { freq: 1046.5, start: 0.14, duration: 0.18, type: 'sine', gain: 0.22 },
  ],
  'close-month': [
    { freq: 523.25, start: 0, duration: 0.11, type: 'triangle', gain: 0.35 },
    { freq: 659.25, start: 0.1, duration: 0.11, type: 'triangle', gain: 0.35 },
    { freq: 783.99, start: 0.2, duration: 0.22, type: 'triangle', gain: 0.4 },
  ],
  swipe: [{ freq: 320, start: 0, duration: 0.07, type: 'sawtooth', gain: 0.12 }],
  error: [
    { freq: 220, start: 0, duration: 0.12, type: 'sine', gain: 0.22 },
    { freq: 196, start: 0.13, duration: 0.16, type: 'sine', gain: 0.18 },
  ],
  celebrate: [
    { freq: 523.25, start: 0, duration: 0.1, type: 'triangle', gain: 0.35 },
    { freq: 659.25, start: 0.08, duration: 0.1, type: 'triangle', gain: 0.35 },
    { freq: 783.99, start: 0.16, duration: 0.1, type: 'triangle', gain: 0.35 },
    { freq: 1046.5, start: 0.24, duration: 0.24, type: 'triangle', gain: 0.4 },
  ],
};

const THROTTLE_MS = 250;

type AudioContextCtor = typeof AudioContext;

let ctx: AudioContext | null = null;
let unlocked = false;
let lastPlayedAt = -Infinity;
let unlockListenersAttached = false;
const activeNotes = new Set<OscillatorNode>();

function getAudioContextCtor(): AudioContextCtor | undefined {
  if (typeof window === 'undefined') return undefined;
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext
  );
}

function getContext(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor = getAudioContextCtor();
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  return ctx;
}

/** Resumes (or lazily creates) the shared AudioContext. Safe to call from any user gesture. */
export function unlockAudio(): void {
  if (unlocked && ctx?.state === 'running') return;
  const context = getContext();
  if (!context) return;
  unlocked = true;
  if (context.state === 'suspended') {
    try {
      void context.resume().catch(() => {
        unlocked = false;
      });
    } catch {
      unlocked = false;
    }
  }
}

/** Call once, e.g. from the app root — arms the first-gesture unlock without playing anything. */
export function attachAudioUnlockListeners(enabled: () => boolean = () => true): void {
  if (unlockListenersAttached || typeof window === 'undefined') return;
  unlockListenersAttached = true;
  const events = ['pointerdown', 'keydown', 'touchstart'] as const;
  const handler = () => {
    if (enabled()) unlockAudio();
  };
  events.forEach((e) => window.addEventListener(e, handler));
}

function playNote(context: AudioContext, note: Note, volume: number): void {
  const oscillator = context.createOscillator();
  const gainNode = context.createGain();
  oscillator.type = note.type ?? 'sine';
  oscillator.frequency.value = note.freq;

  const peak = (note.gain ?? 0.3) * volume;
  const t0 = context.currentTime + note.start;
  gainNode.gain.setValueAtTime(0.0001, t0);
  gainNode.gain.linearRampToValueAtTime(Math.max(peak, 0.0001), t0 + 0.005); // quick attack
  gainNode.gain.exponentialRampToValueAtTime(0.0001, t0 + note.duration); // decay

  oscillator.connect(gainNode).connect(context.destination);
  activeNotes.add(oscillator);
  oscillator.onended = () => {
    activeNotes.delete(oscillator);
    oscillator.disconnect();
    gainNode.disconnect();
  };
  oscillator.start(t0);
  oscillator.stop(t0 + note.duration + 0.02);
}

export interface PlaySoundOptions {
  enabled: boolean;
  volume: number;
  /** Bypasses the 250 ms throttle — only for an explicit "play sample" button in Settings. */
  force?: boolean;
}

/**
 * Plays a named sound if sound is enabled and the volume is audible, throttled to at most one
 * sound per 250 ms (a rapid burst of ticks should not turn into a noise wall).
 */
export function playSound(name: SoundName, options: PlaySoundOptions): void {
  if (!options.enabled || options.volume <= 0) {
    stopSounds();
    return;
  }
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  if (!unlocked) return;
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  if (!options.force && now - lastPlayedAt < THROTTLE_MS) return;

  const context = getContext();
  if (!context) return;
  if (context.state !== 'running') return;

  lastPlayedAt = now;
  try {
    for (const note of SOUNDS[name]) playNote(context, note, Math.min(1, options.volume));
  } catch {
    stopSounds();
  }
}

/** Immediately stop scheduled cues when muted or the tab becomes hidden. */
export function stopSounds() {
  for (const oscillator of activeNotes) {
    try {
      oscillator.stop();
    } catch {
      /* Already ended. */
    }
  }
  activeNotes.clear();
}

/** Test-only: clears module state (context, unlock flag, throttle clock) between test cases. */
export function __resetSfxForTests(): void {
  stopSounds();
  ctx = null;
  unlocked = false;
  lastPlayedAt = -Infinity;
  unlockListenersAttached = false;
}
